import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDrafts } from '../hooks/useDrafts';
import { useBrandMemory } from '../hooks/useBrandMemory';
import { db } from '../firebase/config';
import { getDocs, collection, query, where, limit } from 'firebase/firestore';
import { getCategoryById } from '../data/categories';
import { exportChatToDocx } from '../utils/exportToDocx';
import './Aran.css';

function parseToolMarker(content) {
  const match = content.match(/\[TOOL:([a-z-]+)\]/);
  if (!match) return { text: content, categoryId: null };
  const categoryId = match[1];
  const text = content.replace(/\[TOOL:[a-z-]+\]/g, '').trimEnd();
  return { text, categoryId };
}

const API_URL = process.env.REACT_APP_API_URL || 'https://gormaran-growth-hub-2.onrender.com';

// Category IDs available in /plantillas for tool button routing
// marketing · strategy · content · digital · ecommerce · agency · startup · creative · finance · automation

const ARAN_SYSTEM = `Eres Aran, el asistente de IA de Gormaran — una plataforma para consultoras de marketing que gestionan múltiples marcas.
Ayudas a crear contenido, revisar estrategias, analizar señales y gestionar la cartera de marcas.
Hablas en castellano de España, de tú, con tono directo y profesional. Sin relleno ni formalismos.
Cuando el usuario pregunta sobre contenido o estrategia, ofreces respuestas concretas y accionables.
Cuando el usuario comparte una URL, el sistema ya habrá capturado su contenido y te lo enviará como contexto entre etiquetas [CONTENIDO DE ...]. Analiza ese contenido y úsalo directamente. Nunca digas que no puedes acceder a URLs — el sistema lo hace por ti.

Cuando el usuario pida algo concreto que una herramienta especializada hace mejor que tú (copy de anuncio, plan de medios, propuesta de valor, guión, análisis de competencia, estrategia de precios, etc.), responde brevemente y añade en la ÚLTIMA línea del mensaje, SIN texto adicional, el marcador: [TOOL:categoria]
Donde categoria es la más relevante de: marketing, strategy, content, digital, ecommerce, agency, startup, creative, finance, automation.
Ejemplo correcto: "Para eso tengo una herramienta dedicada que lo genera en 3 campos.\n[TOOL:strategy]"
Nunca expliques el marcador ni lo menciones en el texto. Solo ponlo al final.

REGLA ESPECIAL — PIEZAS DE REDES SOCIALES:
Cuando generes publicaciones para redes sociales (Instagram, LinkedIn, Facebook, TikTok), escribe CADA PIEZA usando este bloque exacto:

[POST]
Meta: descripción corta del contexto (ej: Lunes · Post de producto)
Red: Instagram
Visual: descripción concreta del visual ideal (qué se ve en la imagen, plano, ambiente, estilo)
Caption:
Texto completo de la publicación listo para publicar. Emojis incluidos.
Segunda línea si es necesario.
Hashtags: #tag1 #tag2 #tag3 #tag4 #tag5
[/POST]

- Usa [POST]...[/POST] SOLO para piezas individuales de redes sociales.
- Cada publicación = un bloque [POST] separado.
- El campo Caption incluye todo el texto que iría en la publicación, emojis incluidos.`;

function computeBrainScore(profile) {
  if (!profile || profile.negocio === undefined) return 0;
  const n = profile.negocio || {};
  const a = profile.audiencia || {};
  const v = profile.voz || {};
  const filled = [
    !!(n.nombre?.trim()), !!(n.sector?.trim()), !!(n.propuesta_valor?.trim()),
    n.productos?.length > 0, !!(a.buyer_persona?.trim()), a.dolores?.length > 0,
    !!(v.tono?.trim()), a.objeciones?.length > 0,
  ].filter(Boolean).length;
  return Math.round((filled / 8) * 100);
}

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function applyInline(s) {
  return s
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>');
}

function buildWelcomeMessage({ brainScore, draftsCount, clientsCount, isWorkspace, clientName }) {
  const lines = ['Hola, soy **Aran** ✦ — tu asistente de marketing.\n'];

  if (draftsCount > 0) {
    lines.push(`Tienes **${draftsCount} pieza${draftsCount !== 1 ? 's' : ''}** esperando tu aprobación.`);
  }
  if (clientsCount === 0) {
    lines.push('Aún no tienes marcas. Añade tu primera y aprendo su voz en segundos.');
  } else if (brainScore < 50) {
    const cerebro = isWorkspace && clientName && clientName !== 'la marca'
      ? `Cerebro de ${clientName}`
      : 'Cerebro de cuenta';
    lines.push(`El ${cerebro} está al **${brainScore}%**. Más contexto = mejor contenido.`);
  } else if (isWorkspace && clientName && clientName !== 'la marca') {
    lines.push(`Estoy en el workspace de **${clientName}**. Puedo generar contenido en su voz ahora mismo.`);
  } else {
    lines.push('Todo listo. ¿En qué trabajamos?');
  }

  lines.push('\nPuedo ayudarte con:');
  lines.push('- Estrategia y posicionamiento de marca');
  lines.push('- Borradores de contenido listos para aprobar');
  lines.push('- Emails, posts, anuncios, propuestas de cliente');
  lines.push('- Análisis de tendencias y competencia');

  return lines.join('\n');
}

// Chat history sent to the API: skip error notices and empty turns.
function toHistory(messages) {
  return messages
    .filter(m => !m.isError && typeof m.content === 'string' && m.content.trim())
    .map(m => ({ role: m.role, content: m.content }));
}

function buildStarters({ brainScore, draftsCount, isWorkspace, clientName }) {
  if (draftsCount > 0) {
    return [
      `Resume las ${draftsCount} piezas pendientes de aprobar`,
      'Ideas de contenido para esta semana',
      'Redacta un email de captación',
      '¿Cómo mejoro mis conversiones?',
    ];
  }
  if (isWorkspace && clientName && clientName !== 'la marca') {
    return [
      `Genera un post para ${clientName}`,
      `¿Qué tipo de contenido funciona mejor para ${clientName}?`,
      `Ideas de contenido para ${clientName} esta semana`,
      'Revisa el tono de este mensaje',
    ];
  }
  if (brainScore < 50) {
    return [
      '¿Qué datos necesito para definir bien el tono?',
      'Ayúdame a redactar la propuesta de valor',
      'Ideas de contenido para empezar',
      '¿Cómo aumento las conversiones?',
    ];
  }
  return [
    '¿Cómo aumento las conversiones?',
    'Ideas de contenido para esta semana',
    'Redacta un email de captación',
    'Analiza la estrategia de esta marca',
  ];
}

function renderMarkdown(raw) {
  const lines = escapeHtml(raw).split('\n');
  const out = [];
  let inList = false;

  for (const line of lines) {
    if (/^###\s/.test(line)) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<span class="aran__md-h3">${applyInline(line.slice(4))}</span>`);
    } else if (/^##\s/.test(line)) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<span class="aran__md-h2">${applyInline(line.slice(3))}</span>`);
    } else if (/^[-*•]\s/.test(line)) {
      if (!inList) { out.push('<ul class="aran__md-ul">'); inList = true; }
      out.push(`<li>${applyInline(line.replace(/^[-*•]\s/, ''))}</li>`);
    } else if (line.trim() === '') {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push('<br/>');
    } else {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<span>${applyInline(line)}</span>`);
    }
  }
  if (inList) out.push('</ul>');
  return out.join('\n');
}

export default function Aran({ embedded = false, suggestions = [], onTabChange = null, brandContext = null, brainScore: brainScoreProp = null, onAddToPipeline = null, pendingMessage = null }) {
  const [open, setOpen]         = useState(embedded);
  const [view, setView]         = useState(embedded ? 'chat' : 'home'); // 'home' | 'chat'
  const { currentUser, userProfile: user } = useAuth();
  const location                = useLocation();
  const navigate                = useNavigate();
  const { drafts: allDrafts }   = useDrafts({ estado: 'pendiente_revision' });
  // Inside a brand workspace, counts and starters refer to that brand only
  // (the global count said "20 piezas" on a brand that had 6).
  const wsClientId = location.pathname.startsWith('/client/') ? location.pathname.split('/client/')[1] : null;
  const drafts = wsClientId ? allDrafts.filter(d => d.clientId === wsClientId) : allDrafts;
  const { profile: brandProfile, loading: brandLoading } = useBrandMemory();

  const [hasChannel,  setHasChannel]  = useState(false);
  const [hasWhatsApp, setHasWhatsApp] = useState(false);
  const [guideReady,  setGuideReady]  = useState(false);

  // Chat state
  const [messages,        setMessages]        = useState([]);
  const [input,           setInput]           = useState('');
  const [streaming,       setStreaming]        = useState(false);
  const [streamingText,   setStreamingText]   = useState('');
  const [autoSendTrigger, setAutoSendTrigger] = useState(0);
  const [brandSetupMode,  setBrandSetupMode]  = useState(false);
  const [brainSaved,      setBrainSaved]      = useState(false);
  const [copiedIdx,       setCopiedIdx]       = useState(-1);
  const [buzonSentIdx,    setBuzonSentIdx]    = useState(-1);
  const [pipelineSentIdx, setPipelineSentIdx] = useState(-1);
  const [activeSkillClientId, setActiveSkillClientId] = useState(null);
  const [scContext, setScContext] = useState(() => {
    try {
      const d = JSON.parse(localStorage.getItem('gormaran_sc_current') || 'null');
      return (d && d.ts && Date.now() - d.ts < 7200000) ? d : null;
    } catch { return null; }
  });
  const scContextRef = useRef(scContext);
  useEffect(() => { scContextRef.current = scContext; }, [scContext]);
  const messagesEndRef       = useRef(null);
  const messagesContainerRef = useRef(null);
  const userScrolledRef      = useRef(false);
  const brandContextPropRef  = useRef(brandContext);
  const inputRef           = useRef(null);
  const abortRef           = useRef(null);
  const autoSendRef        = useRef(null);
  const lastDispatchedRef  = useRef(-1);
  const brandContextRef    = useRef(null);
  const skillClientIdRef   = useRef(null);
  const pendingMsgSentRef  = useRef(null);
  const setupPromptRef     = useRef(null);

  useEffect(() => { brandContextPropRef.current = brandContext; }, [brandContext]);

  useEffect(() => {
    function onScResult(e) {
      const d = e.detail;
      if (d && d.rawText) setScContext(d);
    }
    window.addEventListener('sc:result', onScResult);
    return () => window.removeEventListener('sc:result', onScResult);
  }, []);

  // Inject message from Buzón cards into chat
  useEffect(() => {
    const msg = pendingMessage?.text;
    const id  = pendingMessage?.id;
    if (!msg || !id || id === pendingMsgSentRef.current || streaming || !currentUser) return;
    pendingMsgSentRef.current = id;
    const history = toHistory(messages);
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    callChatAPI(msg, history);
  }, [pendingMessage]); // eslint-disable-line

  useEffect(() => {
    if (!currentUser?.uid) return;
    (async () => {
      try {
        const [waSnap, gmailSnap] = await Promise.all([
          getDocs(query(collection(db, 'whatsapp_agents'), where('ownerUid', '==', currentUser.uid), limit(1))),
          getDocs(query(collection(db, 'users', currentUser.uid, 'gmail_connections'), limit(1))),
        ]);
        setHasChannel(!waSnap.empty || !gmailSnap.empty);
        const waPhone = await getDocs(
          query(collection(db, 'whatsapp_agents'), where('ownerUid', '==', currentUser.uid), where('phone', '!=', ''), limit(1))
        );
        setHasWhatsApp(!waPhone.empty);
      } catch {}
      setGuideReady(true);
    })();
  }, [currentUser?.uid]); // eslint-disable-line

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const dist = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (!userScrolledRef.current || dist < 120) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingText]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      const dist = container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledRef.current = dist > 120;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [open, view]);

  // Detect CEREBRO_JSON in completed assistant messages → sync brand brain
  useEffect(() => {
    const lastIdx = messages.length - 1;
    if (lastIdx < 0 || lastDispatchedRef.current === lastIdx) return;
    const last = messages[lastIdx];
    if (last.role !== 'assistant') return;
    const match = last.content.match(/CEREBRO_JSON:\s*(\{[^\n]+\})/);
    if (!match) return;
    lastDispatchedRef.current = lastIdx;
    try {
      const profile = JSON.parse(match[1]);
      const pathname = window.location.pathname;
      const clientId = pathname.startsWith('/client/') ? pathname.split('/client/')[1] : null;
      if (clientId) {
        window.dispatchEvent(new CustomEvent('aran:brand-update', { detail: { clientId, profile } }));
        setBrainSaved(true);
        setTimeout(() => setBrainSaved(false), 4000);
        if (brandSetupMode) {
          setTimeout(() => {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: '✅ Cerebro de Marca guardado.\n\nVe a **Ajustes** y comprueba que la información generada es correcta — puedes editar cualquier campo antes de empezar a crear contenido.',
            }]);
            setBrandSetupMode(false);
          }, 600);
        }
      }
    } catch {}
  }, [messages]);

  // Persist last Aran context to localStorage → Supercomputer picks it up
  useEffect(() => {
    const cid = location.pathname.startsWith('/client/') ? location.pathname.split('/client/')[1] : null;
    if (!cid || messages.length === 0) return;
    const assistantMsgs = messages
      .filter(m => m.role === 'assistant')
      .slice(-3)
      .map(m => m.content.replace(/CEREBRO_JSON[^\n]*/g, '').trim().slice(0, 3000))
      .filter(Boolean);
    if (assistantMsgs.length === 0) return;
    try {
      localStorage.setItem(`gormaran_aran_ctx_${cid}`, JSON.stringify({ ts: Date.now(), msgs: assistantMsgs }));
    } catch {}
  }, [messages, location.pathname]);

  useEffect(() => {
    if (view === 'chat' && open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [view, open]);

  // Sync briefing from AgentSuperPanel — inject as first message + open chat
  useEffect(() => {
    function onBriefing(e) {
      const text = e.detail?.text;
      if (!text) return;
      setMessages([{ role: 'assistant', content: text }]);
      setView('chat');
      setOpen(true);
    }
    window.addEventListener('aran:briefing', onBriefing);
    return () => window.removeEventListener('aran:briefing', onBriefing);
  }, []);

  // Open chat with a prefilled user prompt (skills, brand setup, etc.) and auto-send
  useEffect(() => {
    function onOpenSkill(e) {
      const { prompt, assistantIntro, brandContext, clientId: evtClientId, isBrandSetup } = e.detail || {};
      if (!prompt) return;
      brandContextRef.current = brandContext || null;
      skillClientIdRef.current = evtClientId || null;
      setActiveSkillClientId(evtClientId || null);
      const msgs = [];
      if (assistantIntro) msgs.push({ role: 'assistant', content: assistantIntro });
      // prompt stays internal — never shown as a user message
      // The intro is display-only: a history starting with an assistant turn
      // is rejected by the API, which is what left the brand analysis empty.
      autoSendRef.current = { text: prompt, history: [] };
      setupPromptRef.current = isBrandSetup === true ? prompt : null;
      setBrandSetupMode(isBrandSetup === true);
      setBrainSaved(false);
      userScrolledRef.current = false;
      setMessages(msgs);
      setView('chat');
      setOpen(true);
      setAutoSendTrigger(t => t + 1); // triggers send even in embedded mode
    }
    window.addEventListener('aran:open-skill', onOpenSkill);
    return () => window.removeEventListener('aran:open-skill', onOpenSkill);
  }, []);

  const callChatAPI = useCallback(async (text, history) => {
    setStreaming(true);
    setStreamingText('');
    try {
      const token = await currentUser.getIdToken();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: text, history, systemPrompt: (() => {
          const now = new Date();
          const dateStr = now.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
          const timeStr = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
          let sys = `Fecha y hora actual: ${dateStr}, ${timeStr}.\n\n${ARAN_SYSTEM}`;
          const ctx = brandContextRef.current || brandContextPropRef.current;
          if (ctx) {
            sys += `\n\nCONTEXTO DE MARCA (datos guardados en el Cerebro de Marca — NO preguntes por esta información):\n${ctx}\n\nIMPORTANTE: Ya tienes todos los datos de la marca. Genera el contenido directamente usando estos datos. No hagas preguntas sobre tono, audiencia, sector o propuesta de valor — están en el contexto anterior.`;
          }
          const sc = scContextRef.current;
          if (sc) {
            sys += `\n\nEl usuario tiene actualmente en pantalla el siguiente resultado de Supercomputer${sc.clientName ? ` para ${sc.clientName}` : ''}:\n— Tarea: "${sc.input}"\n— Resultado: ${sc.rawText.slice(0, 1200)}\n\nSi pide guardar en el pipeline o hacer algo con este resultado, úsalo directamente sin preguntar de qué se trata.`;
          }
          if (!ctx) {
            // No brand-specific context: inject full agency overview for Dashboard chat
            try {
              const BRAIN_FIELDS = ['tone', 'audience', 'description', 'nombre', 'sector', 'propuesta_valor'];
              const FIELD_LABELS = { tone: 'tono de voz', audience: 'audiencia objetivo', description: 'descripción', nombre: 'nombre de empresa', sector: 'sector', propuesta_valor: 'propuesta de valor' };
              const clients = JSON.parse(localStorage.getItem('gormaran_clients_v1') || '[]');
              const subBrains = JSON.parse(localStorage.getItem('gormaran_sub_brains') || '[]');
              if (clients.length > 0) {
                const brandsCtx = clients.map(c => {
                  const sb = subBrains.find(s => s.clientId === c.id);
                  const info = sb?.info || {};
                  const clientName = c.name || c.company || '';
                  const efectiveNombre = info.nombre?.trim() || clientName;
                  const filled = BRAIN_FIELDS.filter(f =>
                    f === 'nombre'
                      ? !!efectiveNombre
                      : !!(info[f] && String(info[f]).trim().length > 0)
                  );
                  const score = Math.round(filled.length / BRAIN_FIELDS.length * 100);
                  const missing = BRAIN_FIELDS.filter(f =>
                    f === 'nombre' ? !efectiveNombre : (!info[f] || !String(info[f]).trim())
                  ).map(f => FIELD_LABELS[f]);
                  const dataLines = [
                    efectiveNombre       && `  Empresa: ${efectiveNombre}`,
                    info.sector          && `  Sector: ${info.sector}`,
                    info.propuesta_valor && `  Propuesta de valor: ${info.propuesta_valor}`,
                    info.tone            && `  Tono de voz: ${info.tone}`,
                    info.audience        && `  Audiencia objetivo: ${info.audience}`,
                    info.description     && `  Descripción: ${info.description}`,
                  ].filter(Boolean);
                  return [
                    `MARCA: ${c.name || c.company} (Cerebro al ${score}% — ${filled.length}/${BRAIN_FIELDS.length} campos rellenos)`,
                    ...dataLines,
                    missing.length ? `  ❌ CAMPOS VACÍOS: ${missing.join(' | ')}` : `  ✅ Cerebro completo`,
                  ].join('\n');
                }).join('\n\n');
                sys += `\n\n=== ACCESO COMPLETO AL SISTEMA: CEREBROS DE MARCA ===\nEstos son los datos REALES y ACTUALES del sistema. NUNCA digas que no tienes acceso — tienes acceso total.\n\n${brandsCtx}\n\n=== FIN DATOS DE MARCAS ===\nUsa estos datos directamente. Si el usuario pregunta qué falta en el cerebro de una marca, usa los campos vacíos indicados arriba. Responde con los datos concretos que tienes.`;
              }
            } catch {}
            // Inject Buzón signal summary from localStorage cache
            try {
              const cachedTasks = JSON.parse(localStorage.getItem('gormaran_tasks_cache_v1') || '[]');
              const active = cachedTasks.filter(t => t.status !== 'done').slice(0, 15);
              if (active.length > 0) {
                const summary = active.map(t => {
                  const src  = t.source || 'manual';
                  const name = t.clientName || 'Marca';
                  const prio = t.priority === 'urgent' ? '🔴' : t.priority === 'high' ? '🟠' : '🟡';
                  return `${prio} [${src}] ${t.title || '(sin título)'} · ${name}`;
                }).join('\n');
                sys += `\n\n=== BUZÓN ACTIVO (${active.length} señal${active.length !== 1 ? 'es' : ''} sin resolver) ===\n${summary}\n\nSi el usuario pregunta por el buzón, señales, tareas pendientes o mensajes, usa estos datos. No inventes señales que no estén aquí. El usuario puede acceder al buzón completo en /buzon.`;
              }
            } catch {}
          }
          try {
            const b = JSON.parse(localStorage.getItem('gormaran_last_briefing') || 'null');
            if (b && Date.now() - b.ts < 24 * 60 * 60 * 1000)
              return `${sys}\n\nContexto del último briefing generado:\n${b.text}`;
          } catch {}
          return sys;
        })() }),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error del servidor' }));
        setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${err.error || 'Error del servidor'}`, isError: true }]);
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let streamError = null;
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // SSE events can be split across chunks — only parse complete lines.
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.error) streamError = evt.error;
            else if (evt.text) { accumulated += evt.text; setStreamingText(accumulated); }
          } catch {}
        }
      }
      // Never store an empty assistant turn: it rendered as a blank bubble and
      // poisoned the history (the API rejects empty messages) for every
      // follow-up in the conversation.
      const finalText = accumulated.trim()
        ? accumulated
        : `⚠️ Aran no ha podido responder${streamError ? ` (${streamError.slice(0, 160)})` : ''}. Inténtalo de nuevo.`;
      setMessages(prev => [...prev, { role: 'assistant', content: finalText, ...(!accumulated.trim() && { isError: true }) }]);
      setStreamingText('');
    } catch (err) {
      if (err.name !== 'AbortError')
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Error de conexión. Inténtalo de nuevo.', isError: true }]);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [currentUser]); // eslint-disable-line

  // Trigger auto-send when aran:open-skill fires (works in both embedded and popup modes)
  useEffect(() => {
    if (!autoSendTrigger || !currentUser || !autoSendRef.current) return;
    const { text, history } = autoSendRef.current;
    autoSendRef.current = null;
    callChatAPI(text, history);
  }, [autoSendTrigger, currentUser, callChatAPI]); // eslint-disable-line

  // Proactive welcome message on first open to empty chat
  useEffect(() => {
    if (view === 'chat' && open && messages.length === 0) {
      const ctx = {
        brainScore: brainScoreProp !== null ? brainScoreProp : computeBrainScore(brandProfile),
        draftsCount: drafts.length,
        clientsCount: (() => { try { return JSON.parse(localStorage.getItem('gormaran_clients_v1') || '[]').length; } catch { return 0; } })(),
        isWorkspace: location.pathname.startsWith('/client/'),
        clientName: (() => {
          try {
            const id = location.pathname.split('/client/')[1];
            const cls = JSON.parse(localStorage.getItem('gormaran_clients_v1') || '[]');
            const c = cls.find(x => x.id === id);
            return c?.name || c?.company || 'la marca';
          } catch { return 'la marca'; }
        })(),
      };
      setMessages([{ role: 'assistant', content: buildWelcomeMessage(ctx) }]);
    }
  }, [view, open]); // eslint-disable-line

  const clients      = (() => { try { return JSON.parse(localStorage.getItem('gormaran_clients_v1') || '[]'); } catch { return []; } })();
  const sessionCount = (() => { try { return JSON.parse(localStorage.getItem('gormaran_sessions_v2') || '[]').length; } catch { return 0; } })();
  const brainScore   = brainScoreProp !== null ? brainScoreProp : computeBrainScore(brandProfile);

  const isWorkspace = location.pathname.startsWith('/client/');
  const isGenerate  = location.pathname === '/generate';
  const isDashboard = location.pathname === '/dashboard';
  const clientId    = isWorkspace ? location.pathname.split('/client/')[1] : null;
  const currentClient = clientId ? clients.find(c => c.id === clientId) : null;
  const clientName  = currentClient?.name || currentClient?.company || 'la marca';

  const steps = [
    { label: 'Cerebro de cuenta', done: brainScore > 0,     action: () => navigate('/dashboard') },
    { label: 'Primera marca',     done: clients.length > 0, action: () => navigate('/dashboard') },
    { label: 'Canal conectado',   done: hasChannel,         action: () => navigate('/settings')  },
    { label: 'Primera pieza',     done: sessionCount > 0,   action: () => navigate('/generate')  },
    { label: 'WhatsApp activo',   done: hasWhatsApp,        action: () => navigate('/settings')  },
  ];
  const stepsDone    = steps.filter(s => s.done).length;
  const guideComplete = stepsDone === steps.length;

  let topSuggestion;
  if (drafts.length > 0) {
    topSuggestion = { icon: '✅', text: `${drafts.length} pieza${drafts.length !== 1 ? 's' : ''} esperan tu aprobación`, action: () => navigate('/dashboard') };
  } else if (!brandLoading && brainScore < 60) {
    topSuggestion = { icon: '🧠', text: `Cerebro al ${brainScore}% — añade más datos para afinar el tono`, action: () => navigate('/dashboard') };
  } else if (!guideComplete && guideReady) {
    topSuggestion = { icon: '📋', text: `${stepsDone} de 5 pasos completados`, action: () => navigate('/dashboard') };
  } else {
    topSuggestion = { icon: '⚡', text: isWorkspace ? `¿Generamos contenido para ${clientName}?` : '¿Qué herramienta usamos hoy?', action: () => navigate('/plantillas') };
  }

  const quickActions = [
    { icon: '⚡', label: 'Herramientas', to: '/plantillas' },
    ...(drafts.length > 0 ? [{ icon: '✅', label: `Aprobar (${drafts.length})`, to: '/dashboard' }] : []),
    ...(isWorkspace        ? [{ icon: '🏠', label: 'Dashboard',                  to: '/dashboard' }] : []),
    ...(!isWorkspace && clients.length > 0 ? [{ icon: '🏷', label: clients[0]?.name || 'Marca', to: `/client/${clients[0]?.id}` }] : []),
    { icon: '⚙️', label: 'Ajustes',   to: '/settings'  },
  ].slice(0, 4);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming || !currentUser) return;
    const history = toHistory(messages);
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');

    // Detect URLs and fetch their content to include as context
    const urlRe = /https?:\/\/[^\s"'<>)]+/g;
    const urls = [...new Set(text.match(urlRe) || [])].slice(0, 2);
    let enrichedText = text;
    if (urls.length > 0) {
      try {
        const token = await currentUser.getIdToken();
        const fetched = await Promise.all(urls.map(url =>
          fetch(`${API_URL}/api/ai/fetch-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ url }),
          }).then(r => r.ok ? r.json() : null).catch(() => null)
        ));
        const blocks = fetched
          .filter(Boolean)
          .map(r => `[CONTENIDO DE ${r.url}]\n${r.text}\n[/CONTENIDO]`);
        if (blocks.length > 0) {
          enrichedText = `${blocks.join('\n\n')}\n\n${text}`;
        }
      } catch { /* send without enrichment if fetch fails */ }
    }

    await callChatAPI(enrichedText, history);
  }, [input, streaming, currentUser, messages, callChatAPI]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setOpen(false);
    setBrandSetupMode(false);
    brandContextRef.current = null;
    skillClientIdRef.current = null;
    setActiveSkillClientId(null);
  };

  const openChat = () => {
    setView('chat');
    setOpen(true);
  };

  const handleExport = useCallback(async () => {
    if (streaming) return;
    let exportName = clientName;
    if ((!exportName || exportName === 'la marca') && skillClientIdRef.current) {
      try {
        const cls = JSON.parse(localStorage.getItem('gormaran_clients_v1') || '[]');
        const c = cls.find(x => x.id === skillClientIdRef.current);
        if (c) exportName = c.name || c.company || exportName;
      } catch {}
    }
    await exportChatToDocx(messages, exportName);
  }, [streaming, messages, clientName]);

  const handleSaveBrain = () => {
    if (streaming) return;
    const saveInstruction = 'Guarda el perfil completo con todos los datos recogidos hasta ahora. Al final de tu respuesta incluye exactamente en una sola línea:\nCEREBRO_JSON: {"tone":"[tono]","audience":"[audiencia]","sector":"[sector]","propuesta_valor":"[propuesta de valor]","description":"[descripción breve de la marca]"}';
    const history = toHistory(messages);
    setMessages(prev => [...prev, { role: 'user', content: '💾 Guardar en Cerebro de Marca' }]);
    callChatAPI(saveInstruction, history);
  };

  const handleAddToBuzon = useCallback(async (msgText, msgIdx) => {
    const cid = clientId || skillClientIdRef.current;
    if (!cid || !currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_URL}/api/tasks/${cid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: msgText.slice(0, 2000) }),
      });
      if (res.ok) {
        setBuzonSentIdx(msgIdx);
        setTimeout(() => setBuzonSentIdx(s => s === msgIdx ? -1 : s), 3000);
      }
    } catch {}
  }, [clientId, currentUser]); // eslint-disable-line

  const handleAddToPipeline = useCallback(async (msgText, msgIdx) => {
    const canPipeline = onAddToPipeline || skillClientIdRef.current;
    if (!canPipeline) return;
    const META_RE = /^(aquí tienes|te propongo|a continuación|elige|puedo|aquí van|claro|por supuesto|perfecto|entendido|de acuerdo|con gusto|aquí te|como me|estas son|estas opciones)/i;
    const lines = msgText.split('\n').map(l => l.trim()).filter(Boolean);
    let title = '';
    for (const l of lines) {
      const h = l.replace(/^\*\*/, '').replace(/\*\*$/, '').replace(/^#{1,3}\s*/, '').trim();
      if (h.length >= 4 && h.length <= 80 && !META_RE.test(h)) { title = h.slice(0, 60); break; }
    }
    if (!title) {
      for (const l of lines) {
        const c = l.replace(/[#*`>\-]/g, '').trim();
        if (c.length >= 4 && !META_RE.test(c)) { title = c.slice(0, 60); break; }
      }
    }
    title = title || 'Contenido generado por Aran';
    if (clientName && clientName !== 'la marca' && !title.includes(clientName)) {
      title = `${clientName} — ${title}`.slice(0, 80);
    }
    const firstReal = lines.findIndex(l => !META_RE.test(l) && l.replace(/[#*`>\-]/g, '').trim().length >= 4);
    const notes = lines.slice(Math.max(firstReal, 0)).join('\n').slice(0, 2000);
    const projectData = {
      id: crypto.randomUUID(),
      title,
      type: 'content',
      priority: 'medium',
      status: 'todo',
      notes,
      createdAt: Date.now(),
    };
    if (onAddToPipeline) {
      onAddToPipeline(projectData);
    } else {
      const cid = skillClientIdRef.current;
      if (cid && currentUser) {
        try {
          const token = await currentUser.getIdToken();
          await fetch(`${API_URL}/api/pipeline/${cid}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(projectData),
          });
          const key = `gormaran_projects_${cid}`;
          const existing = JSON.parse(localStorage.getItem(key) || '[]');
          localStorage.setItem(key, JSON.stringify([projectData, ...existing]));
        } catch {}
      }
    }
    setPipelineSentIdx(msgIdx);
    setTimeout(() => setPipelineSentIdx(s => s === msgIdx ? -1 : s), 3000);
    if (onTabChange) onTabChange('contenido');
  }, [onAddToPipeline, onTabChange, currentUser]); // eslint-disable-line

  if (!user) return null;

  return (
    <div className={`aran${embedded ? ' aran--embedded' : ''}`}>
      {!embedded && (
        <button
          className={`aran__btn${open ? ' aran__btn--open' : ''}`}
          onClick={() => {
            if (open) { handleClose(); }
            else { setView('chat'); setOpen(true); }
          }}
          aria-label={open ? 'Cerrar Aran' : 'Abrir chat con Aran'}
        >
          <span className="aran__btn-icon">{open ? '✕' : '✦'}</span>
          {!open && drafts.length > 0 && (
            <span className="aran__btn-badge">{drafts.length > 9 ? '9+' : drafts.length}</span>
          )}
        </button>
      )}

      <div className={`aran__panel${open ? ' aran__panel--open' : ''}${view === 'chat' ? ' aran__panel--chat' : ''}`} aria-hidden={!open}>

        {/* ── HOME VIEW ── */}
        {view === 'home' && (
          <>
            <div className="aran__hd">
              <div className="aran__hd-logo">✦</div>
              <div className="aran__hd-info">
                <span className="aran__hd-name">Aran</span>
                <span className="aran__hd-ctx">
                  {isDashboard ? 'Dashboard' : isWorkspace ? clientName : isGenerate ? 'Generador' : 'Gormaran'}
                </span>
              </div>
              <button className="aran__chat-toggle" onClick={openChat} title="Abrir chat con Aran">
                💬
              </button>
            </div>

            <button className="aran__suggestion" onClick={() => { topSuggestion.action(); handleClose(); }}>
              <span className="aran__suggestion-icon">{topSuggestion.icon}</span>
              <span className="aran__suggestion-text">{topSuggestion.text}</span>
              <span className="aran__suggestion-arrow">→</span>
            </button>

            <div className="aran__section">
              <p className="aran__section-lbl">Acceder a</p>
              <div className="aran__actions">
                {quickActions.map((a, i) => (
                  <button key={i} className="aran__action" onClick={() => { navigate(a.to); handleClose(); }}>
                    <span className="aran__action-icon">{a.icon}</span>
                    <span className="aran__action-label">{a.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {!guideComplete && (
              <div className="aran__section">
                <div className="aran__checklist-hd">
                  <p className="aran__section-lbl">Configuración</p>
                  <span className="aran__checklist-count">{stepsDone}/5</span>
                </div>
                <div className="aran__checklist">
                  {steps.map((s, i) => (
                    <button key={i} className={`aran__check${s.done ? ' aran__check--done' : ''}`} onClick={() => { s.action(); handleClose(); }}>
                      <span className="aran__check-icon">{s.done ? '✓' : i + 1}</span>
                      <span className="aran__check-label">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="aran__footer">
              <button className="aran__gen-cta" onClick={openChat}>
                💬 Pregúntale a Aran
              </button>
            </div>
          </>
        )}

        {/* ── CHAT VIEW ── */}
        {view === 'chat' && (
          <>
            <div className="aran__hd">
              {!embedded && <button className="aran__back" onClick={() => setView('home')} aria-label="Volver">←</button>}
              <div className="aran__hd-logo">✦</div>
              <div className="aran__hd-info">
                <span className="aran__hd-name">Aran {embedded && isWorkspace ? `· ${clientName}` : '· Chat'}</span>
                <span className="aran__hd-ctx">IA de Gormaran</span>
              </div>
            </div>

            {/* ── Proactive suggestions (embedded only, before any user message) ── */}
            {embedded && suggestions.length > 0 && !messages.some(m => m.role === 'user') && !streaming && !brandSetupMode && (
              <div className="aran__suggestions">
                {suggestions.map(s => (
                  <button
                    key={s.id}
                    className="aran__suggestion-card"
                    onClick={() => {
                      if (s.type === 'tab') {
                        onTabChange?.(s.tab);
                      } else {
                        const history = toHistory(messages);
                        setMessages(prev => [...prev, { role: 'user', content: s.action }]);
                        callChatAPI(s.prompt, history);
                      }
                    }}
                  >
                    <span className="aran__suggestion-card__text">{s.text}</span>
                    <span className="aran__suggestion-card__cta">{s.action} →</span>
                  </button>
                ))}
              </div>
            )}

            <div className="aran__messages" ref={messagesContainerRef}>
              {messages.length === 0 && !streaming && (
                <div className="aran__empty-chat">
                  <span className="aran__empty-icon">✦</span>
                  <p>Cargando…</p>
                </div>
              )}

              {messages.map((m, i) => {
                const displayContent = m.role === 'assistant'
                  ? m.content.replace(/\nCEREBRO_JSON:[^\n]*/g, '').replace(/^CEREBRO_JSON:[^\n]*/gm, '').trim()
                  : m.content;
                const { text, categoryId } = m.role === 'assistant'
                  ? parseToolMarker(displayContent)
                  : { text: displayContent, categoryId: null };
                const cat = categoryId ? getCategoryById(categoryId) : null;
                const isLastAssistant = m.role === 'assistant' && i === messages.length - 1 && !streaming && !m.isError;
                return (
                  <div key={i} className={`aran__msg aran__msg--${m.role}`}>
                    <div
                      className="aran__msg-bubble"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }}
                    />
                    {cat && !embedded && (
                      <button
                        className="aran__tool-btn"
                        onClick={() => { navigate(`/category/${cat.id}`); handleClose(); }}
                      >
                        <span className="aran__tool-btn-icon">{cat.icon}</span>
                        <span>{cat.name}</span>
                        <span className="aran__tool-btn-arrow">→</span>
                      </button>
                    )}
                    {isLastAssistant && (
                      <div className="aran__msg-actions">
                        <button
                          className="aran__copy-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(text);
                            setCopiedIdx(i);
                            setTimeout(() => setCopiedIdx(c => c === i ? -1 : c), 2000);
                          }}
                          title="Copiar texto"
                        >
                          {copiedIdx === i ? '✓ Copiado' : '⎘ Copiar'}
                        </button>
                        {(embedded ? onAddToPipeline : activeSkillClientId) && (
                          <button
                            className={`aran__copy-btn${pipelineSentIdx === i ? ' aran__copy-btn--sent' : ''}`}
                            onClick={() => handleAddToPipeline(text, i)}
                            title="Añadir al pipeline de contenido"
                            disabled={pipelineSentIdx === i}
                          >
                            {pipelineSentIdx === i ? '✓ En Pipeline' : '→ Pipeline'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {streaming && (
                <div className="aran__msg aran__msg--assistant">
                  <div className="aran__msg-bubble">
                    {streamingText
                      ? <span dangerouslySetInnerHTML={{ __html: renderMarkdown(parseToolMarker(streamingText.replace(/\nCEREBRO_JSON:[^\n]*/g, '').replace(/^CEREBRO_JSON:[^\n]*/gm, '')).text) }} />
                      : <span className="aran__typing"><span /><span /><span /></span>
                    }
                  </div>
                </div>
              )}

              {!messages.some(m => m.role === 'user') && !streaming && messages.length > 0 && !brandSetupMode && (
                <div className="aran__starters">
                  {buildStarters({ brainScore, draftsCount: drafts.length, isWorkspace, clientName }).map(s => (
                    <button key={s} className="aran__starter" onClick={() => { setInput(s); inputRef.current?.focus(); }}>
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {messages.some(m => m.role === 'assistant') && (
              <div className="aran__save-bar">
                {brandSetupMode ? (
                  brainSaved ? (
                    <span className="aran__save-ok">✅ Cerebro de Marca actualizado</span>
                  ) : messages[messages.length - 1]?.isError && setupPromptRef.current ? (
                    <button
                      className="aran__save-btn"
                      disabled={streaming}
                      onClick={() => {
                        setMessages(prev => prev.filter(m => !m.isError));
                        callChatAPI(setupPromptRef.current, []);
                      }}
                    >
                      ↻ Reintentar análisis
                    </button>
                  ) : (
                    <button className="aran__save-btn" onClick={handleSaveBrain} disabled={streaming}>
                      💾 Guardar en Cerebro de Marca
                    </button>
                  )
                ) : (
                  <>
                    {(embedded ? onAddToPipeline : activeSkillClientId) && messages.some(m => m.role === 'user') && !streaming && (
                      <button
                        className="aran__save-btn"
                        disabled={streaming}
                        onClick={() => {
                          const lastAss = [...messages].reverse().find(m => m.role === 'assistant');
                          if (lastAss) {
                            const idx = messages.lastIndexOf(lastAss);
                            handleAddToPipeline(lastAss.content, idx);
                          }
                        }}
                      >
                        → Añadir al Pipeline
                      </button>
                    )}
                    {scContext && !streaming && scContext.clientId && (
                      <button
                        className="aran__save-btn"
                        onClick={async () => {
                          try {
                            const cid = scContext.clientId;
                            const title = scContext.input.slice(0, 80);
                            const projectData = { id: `sc_${Date.now()}`, title, notes: scContext.rawText.slice(0, 800), type: 'research', status: 'todo', createdAt: new Date().toISOString() };
                            if (currentUser) {
                              const token = await currentUser.getIdToken();
                              await fetch(`${API_URL}/api/pipeline/${cid}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(projectData) });
                            }
                            const key = `gormaran_projects_${cid}`;
                            const existing = JSON.parse(localStorage.getItem(key) || '[]');
                            localStorage.setItem(key, JSON.stringify([projectData, ...existing]));
                            if (onTabChange) onTabChange('contenido');
                          } catch {}
                        }}
                      >
                        → SC al Pipeline
                      </button>
                    )}
                  </>
                )}
                <button
                  className="aran__export-btn"
                  onClick={handleExport}
                  disabled={streaming}
                  title="Descargar informe en Word"
                >
                  📄 Descargar informe
                </button>
              </div>
            )}

            <div className="aran__input-bar">
              <textarea
                ref={inputRef}
                className="aran__input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pregunta lo que sea…"
                rows={1}
                disabled={streaming}
              />
              <button
                className="aran__send"
                onClick={sendMessage}
                disabled={!input.trim() || streaming}
                aria-label="Enviar"
              >
                {streaming ? '⏳' : '↑'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
